import { Device } from "@devices/types";
import { FileInfo } from "@files/types";
import { compareVersionCodes } from "@files/utils/apkStatus";

/**
 * Installed version codes of every package across a group's devices, keyed by lowercased package name
 * and then device id.
 *
 * Built once per device list because a device can report hundreds of packages: scanning those lists per
 * version row would repeat the whole sweep on every render of the panel.
 */
export type InstalledPackageIndex = {
    byPackage: Map<string, Map<number, number>>;
};

export const buildInstalledPackageIndex = (devices: Device[]): InstalledPackageIndex => {
    const byPackage = new Map<string, Map<number, number>>();

    for (const device of devices) {
        const packages = device.lastState?.packages;

        if (!packages) {
            continue;
        }

        for (const item of packages) {
            const key = item.package.toLowerCase();
            let versionByDevice = byPackage.get(key);

            if (!versionByDevice) {
                versionByDevice = new Map<number, number>();
                byPackage.set(key, versionByDevice);
            }
            versionByDevice.set(device.id, Number(item.versionCode));
        }
    }

    return { byPackage };
};

export type GroupApkTargets = {
    /** The devices this exact version may be sent to. Devices on a newer version are never included. */
    eligible: Device[];
    upToDateCount: number;
    newerCount: number;
    totalDevices: number;
    /** At least one eligible device already runs an older version, so this reads as an update, not an install. */
    hasOutdatedInstall: boolean;
};

export const getGroupApkTargets = (file: FileInfo, devices: Device[], index: InstalledPackageIndex): GroupApkTargets => {
    const packageName = file.metadata?.file?.apk?.manifest?.package?.toLowerCase();
    const targetVersionCode = Number(file.metadata?.file?.apk?.manifest?.versioncode);
    const installedByDevice = packageName ? index.byPackage.get(packageName) : undefined;

    const eligible: Device[] = [];
    let upToDateCount = 0;
    let newerCount = 0;
    let hasOutdatedInstall = false;

    for (const device of devices) {
        // A device that never reported a package list has no entry here, so it reads the same as a device
        // without the package and is assumed to need the install: an install cannot downgrade it, and a group
        // of only such devices must not end up with a permanently dead button.
        const installedVersionCode = installedByDevice?.get(device.id);

        switch (compareVersionCodes(installedVersionCode, targetVersionCode)) {
            case "toUpdate":
                hasOutdatedInstall = true;
                eligible.push(device);
                break;
            case "toInstall":
                eligible.push(device);
                break;
            case "currentVersion":
                upToDateCount++;
                break;
            case "outdatedVersion":
                newerCount++;
                break;
        }
    }

    return { eligible, upToDateCount, newerCount, totalDevices: devices.length, hasOutdatedInstall };
};

export const getNoEligibleDevicesTooltipKey = (targets: GroupApkTargets) =>
    targets.newerCount > 0
        ? "sidepanels:groupPanel.tooltip.newerVersionOnAllDevices"
        : "sidepanels:groupPanel.tooltip.currentVersionOnAllDevices";
